/**
 * Mouvement (en pixels écran) toléré avant qu'un geste ne bascule en drag.
 * En dessous, le geste reste un clic : sans ce seuil, le moindre tremblement
 * de la souris ou du doigt pendant un tap posait `dragged` et le clic sur le
 * pays était ignoré silencieusement (aucun feedback pour le joueur).
 * Le tactile est plus tolérant — un doigt bouge toujours un peu plus.
 */
const DRAG_THRESHOLD_MOUSE = 5;
const DRAG_THRESHOLD_TOUCH = 10;

export default class MapInteraction {
    constructor(mapContainer, mapSvg, options = {}) {
        this.mapContainer = mapContainer;
        this.mapSvg = mapSvg;
        this.zoomStep = options.zoomStep || 0.05;
        this.maxZoom = options.maxZoom || 300;
        this.dragged = false;

        const vb = mapSvg.viewBox.baseVal;
        this.baseViewBox = { x: vb.x, y: vb.y, w: vb.width, h: vb.height };
        this.vb = { x: vb.x, y: vb.y, w: vb.width, h: vb.height };
        this.state = { isDragging: false, startX: 0, startY: 0, startVbX: 0, startVbY: 0, threshold: DRAG_THRESHOLD_MOUSE };
        this._pinch = { active: false, startDist: 0, startVb: null, centerX: 0, centerY: 0 };
        this._anim = null;

        this._applyViewBox();
        this._bindEvents();
    }

    _applyViewBox() {
        this.mapSvg.setAttribute("viewBox", `${this.vb.x} ${this.vb.y} ${this.vb.w} ${this.vb.h}`);
    }

    _clampViewBox() {
        const base = this.baseViewBox;
        const minW = base.w / this.maxZoom;
        const minH = base.h / this.maxZoom;
        this.vb.w = Math.max(minW, Math.min(base.w, this.vb.w));
        this.vb.h = Math.max(minH, Math.min(base.h, this.vb.h));

        const maxX = base.x + base.w - this.vb.w;
        const maxY = base.y + base.h - this.vb.h;
        const padX = this.vb.w * 0.05;
        const padY = this.vb.h * 0.05;
        this.vb.x = Math.max(base.x - padX, Math.min(maxX + padX, this.vb.x));
        this.vb.y = Math.max(base.y - padY, Math.min(maxY + padY, this.vb.y));
    }

    _clientToSvg(clientX, clientY) {
        const pt = this.mapSvg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        return pt.matrixTransform(this.mapSvg.getScreenCTM().inverse());
    }

    _getTouchDistance(t1, t2) {
        const dx = t1.clientX - t2.clientX;
        const dy = t1.clientY - t2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    _getTouchCenter(t1, t2) {
        return {
            x: (t1.clientX + t2.clientX) / 2,
            y: (t1.clientY + t2.clientY) / 2
        };
    }

    _bindEvents() {
        const startDragging = (clientX, clientY, threshold = DRAG_THRESHOLD_MOUSE) => {
            this.state.isDragging = true;
            this.state.startX = clientX;
            this.state.startY = clientY;
            this.state.startVbX = this.vb.x;
            this.state.startVbY = this.vb.y;
            this.state.threshold = threshold;
            // Capture the true pixel/SVG-unit scale from the browser's CTM so that
            // pan speed stays 1:1 regardless of preserveAspectRatio letterboxing.
            const ctm = this.mapSvg.getScreenCTM();
            this.state.scaleX = ctm ? Math.abs(ctm.a) : this.mapContainer.getBoundingClientRect().width / this.vb.w;
            this.state.scaleY = ctm ? Math.abs(ctm.d) : this.mapContainer.getBoundingClientRect().height / this.vb.h;
            this.dragged = false;
        };

        const moveDragging = (clientX, clientY) => {
            if (!this.state.isDragging) return;
            const dxPx = clientX - this.state.startX;
            const dyPx = clientY - this.state.startY;
            // Le flag ne bascule qu'au-delà du seuil, et ne redescend jamais avant
            // le prochain appui : revenir au point de départ après avoir pané ne
            // doit pas réhabiliter le clic. Le pan, lui, reste continu dès le
            // premier pixel — la carte suit le curseur sans à-coup.
            if (!this.dragged && Math.hypot(dxPx, dyPx) > this.state.threshold) this.dragged = true;
            const dx = dxPx / this.state.scaleX;
            const dy = dyPx / this.state.scaleY;
            this.vb.x = this.state.startVbX - dx;
            this.vb.y = this.state.startVbY - dy;
            this._clampViewBox();
            this._applyViewBox();
        };

        this.mapContainer.addEventListener("wheel", (e) => {
            e.preventDefault();
            const base = this.baseViewBox;
            const minW = base.w / this.maxZoom;
            const zoomingIn = e.deltaY < 0;
            if (zoomingIn && this.vb.w <= minW) return;
            if (!zoomingIn && this.vb.w >= base.w) return;

            const svgPoint = this._clientToSvg(e.clientX, e.clientY);
            const factor = zoomingIn ? (1 - this.zoomStep) : (1 + this.zoomStep);
            const newW = Math.max(minW, Math.min(base.w, this.vb.w * factor));
            const newH = Math.max(base.h / this.maxZoom, Math.min(base.h, this.vb.h * factor));
            const actualFactor = newW / this.vb.w;

            this.vb.x = svgPoint.x - (svgPoint.x - this.vb.x) * actualFactor;
            this.vb.y = svgPoint.y - (svgPoint.y - this.vb.y) * actualFactor;
            this.vb.w = newW;
            this.vb.h = newH;

            this._clampViewBox();
            this._applyViewBox();
        }, { passive: false });

        this._onMouseMove = (e) => moveDragging(e.clientX, e.clientY);
        this._onMouseUp = () => this.state.isDragging = false;

        this.mapContainer.addEventListener("mousedown", (e) => startDragging(e.clientX, e.clientY));
        window.addEventListener("mousemove", this._onMouseMove);
        window.addEventListener("mouseup", this._onMouseUp);

        this.mapContainer.addEventListener("touchstart", (e) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                this.state.isDragging = false;
                this._pinch = {
                    active: true,
                    startDist: this._getTouchDistance(e.touches[0], e.touches[1]),
                    startVb: { ...this.vb },
                    centerX: 0,
                    centerY: 0
                };
                const center = this._getTouchCenter(e.touches[0], e.touches[1]);
                const svgCenter = this._clientToSvg(center.x, center.y);
                this._pinch.centerX = svgCenter.x;
                this._pinch.centerY = svgCenter.y;
            } else if (e.touches.length === 1 && !this._pinch.active) {
                startDragging(e.touches[0].clientX, e.touches[0].clientY, DRAG_THRESHOLD_TOUCH);
            }
        }, { passive: false });

        this._onTouchMove = (e) => {
            if (e.touches.length === 2 && this._pinch.active) {
                e.preventDefault();
                const dist = this._getTouchDistance(e.touches[0], e.touches[1]);
                const ratio = this._pinch.startDist / dist;
                const sv = this._pinch.startVb;
                const base = this.baseViewBox;
                const minW = base.w / this.maxZoom;

                const newW = Math.max(minW, Math.min(base.w, sv.w * ratio));
                const newH = Math.max(base.h / this.maxZoom, Math.min(base.h, sv.h * ratio));
                const actualRatio = newW / sv.w;

                this.vb.w = newW;
                this.vb.h = newH;
                this.vb.x = this._pinch.centerX - (this._pinch.centerX - sv.x) * actualRatio;
                this.vb.y = this._pinch.centerY - (this._pinch.centerY - sv.y) * actualRatio;

                this._clampViewBox();
                this._applyViewBox();
                this.dragged = true;
            } else if (e.touches.length === 1 && !this._pinch.active) {
                moveDragging(e.touches[0].clientX, e.touches[0].clientY);
            }
        };

        this._onTouchEnd = (e) => {
            if (this._pinch.active && e.touches.length < 2) {
                this._pinch.active = false;
                if (e.touches.length === 1) {
                    startDragging(e.touches[0].clientX, e.touches[0].clientY, DRAG_THRESHOLD_TOUCH);
                    // startDragging repart de `dragged = false` : on le rétablit, un
                    // pinch reste une manipulation de la carte. Sans ça, le doigt
                    // restant — qui ne franchira pas forcément le seuil avant de se
                    // lever — laisserait passer un clic sur le pays survolé.
                    this.dragged = true;
                }
            }
            if (e.touches.length === 0) {
                this.state.isDragging = false;
                this._pinch.active = false;
            }
        };

        window.addEventListener("touchmove", this._onTouchMove, { passive: false });
        window.addEventListener("touchend", this._onTouchEnd);
    }

    get scale() {
        return this.baseViewBox.w / this.vb.w;
    }

    zoomToElement(element, scale = null, duration = 1.5) {
        if (!element) return;
        if (this._anim) this._anim.kill();

        const bbox = element.getBBox();
        const cx = bbox.x + bbox.width / 2;
        const cy = bbox.y + bbox.height / 2;
        const base = this.baseViewBox;

        if (scale === null) {
            // Target: country fills ~50% of the viewBox in its largest dimension.
            const aspect = base.w / base.h;
            const effectiveW = Math.max(bbox.width, bbox.height * aspect);
            scale = Math.min(this.maxZoom, Math.max(4, (base.w * 0.5) / effectiveW));
        }

        const targetW = base.w / scale;
        const targetH = base.h / scale;
        const targetX = cx - targetW / 2;
        const targetY = cy - targetH / 2;

        const proxy = { x: this.vb.x, y: this.vb.y, w: this.vb.w, h: this.vb.h };
        this._anim = gsap.to(proxy, {
            duration,
            x: targetX,
            y: targetY,
            w: targetW,
            h: targetH,
            ease: "power2.inOut",
            onUpdate: () => {
                this.vb.x = proxy.x;
                this.vb.y = proxy.y;
                this.vb.w = proxy.w;
                this.vb.h = proxy.h;
                this._applyViewBox();
            }
        });
    }

    /**
     * Animate the viewBox to frame an arbitrary region (e.g. the union of a
     * continent's country paths). Clamped to the base viewBox so it never
     * over-zooms beyond the map bounds.
     * @param {{x:number,y:number,width:number,height:number}} bbox Region to frame, in SVG units.
     * @param {number} [padding] Fraction of the region added as margin on each side.
     * @param {number} [duration] Animation duration in seconds.
     */
    zoomToBBox(bbox, padding = 0.15, duration = 1) {
        if (!bbox || bbox.width <= 0 || bbox.height <= 0) return;
        if (this._anim) this._anim.kill();

        const base = this.baseViewBox;
        const cx = bbox.x + bbox.width / 2;
        const cy = bbox.y + bbox.height / 2;

        // Fit the region (with padding) while preserving the map's aspect ratio.
        const aspect = base.w / base.h;
        let targetW = bbox.width * (1 + padding * 2);
        let targetH = bbox.height * (1 + padding * 2);
        if (targetW / targetH > aspect) {
            targetH = targetW / aspect;
        } else {
            targetW = targetH * aspect;
        }

        const minW = base.w / this.maxZoom;
        targetW = Math.max(minW, Math.min(base.w, targetW));
        targetH = Math.max(base.h / this.maxZoom, Math.min(base.h, targetH));

        const targetX = cx - targetW / 2;
        const targetY = cy - targetH / 2;

        const proxy = { x: this.vb.x, y: this.vb.y, w: this.vb.w, h: this.vb.h };
        this._anim = gsap.to(proxy, {
            duration,
            x: targetX,
            y: targetY,
            w: targetW,
            h: targetH,
            ease: "power2.inOut",
            onUpdate: () => {
                this.vb.x = proxy.x;
                this.vb.y = proxy.y;
                this.vb.w = proxy.w;
                this.vb.h = proxy.h;
                this._clampViewBox();
                this._applyViewBox();
            }
        });
    }

    reset() {
        if (this._anim) this._anim.kill();
        const base = this.baseViewBox;
        const proxy = { x: this.vb.x, y: this.vb.y, w: this.vb.w, h: this.vb.h };
        this._anim = gsap.to(proxy, {
            duration: 0.4,
            x: base.x,
            y: base.y,
            w: base.w,
            h: base.h,
            ease: "power2.out",
            onUpdate: () => {
                this.vb.x = proxy.x;
                this.vb.y = proxy.y;
                this.vb.w = proxy.w;
                this.vb.h = proxy.h;
                this._applyViewBox();
            }
        });
    }

    destroy() {
        if (this._anim) this._anim.kill();
        window.removeEventListener("mousemove", this._onMouseMove);
        window.removeEventListener("mouseup", this._onMouseUp);
        window.removeEventListener("touchmove", this._onTouchMove);
        window.removeEventListener("touchend", this._onTouchEnd);
    }
}
